import { RiCircleLine, RiRadioButtonLine, RiSubtractLine } from "@remixicon/react";
import classNames from "classnames";
import React, {
    InputHTMLAttributes,
    ReactElement,
    ReactNode,
} from "react";
import { RiCheckLine, RiCloseLine } from "react-icons/ri";

export const SIZES = ["small", "medium", "large"] as const;
export const VARIANT = ["title", "icon", "none", "radio", "icon-boxes"] as const;
export const TOGGLE_VARIANT = ["primary", "secondary"] as const;

export interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
    size?: typeof SIZES[number];
    variant?: typeof VARIANT[number];
    isIntermediate?: boolean;
    text?: ReactNode;
    textPlacement?: "start" | "end";
    labelClassName?: string;
    textVariant?: typeof TOGGLE_VARIANT[number];
}

const Toggle = React.forwardRef((
    args: ToggleProps,
    ref: React.ForwardedRef<HTMLInputElement>,
): ReactElement => {
    const {
        size, variant, className, labelClassName, text, textPlacement, textVariant, isIntermediate, ...rest
    } = args;

    return (
        <label className={labelClassName}>
            {textPlacement === "start" && text
                ? <span className={classNames(`toggle-text-${textPlacement}`, `toggle-text-${textVariant}`)}>{text}</span>
                : undefined}
            <span className={classNames(className, "toggle", size ? `toggle-${size}` : "", variant ? `toggle-${variant}` : "")}>

                <input
                    ref={ref}
                    type="checkbox"
                    tabIndex={0}
                    {...rest}
                />
                <span className="slider" />
                {variant === "title" && (
                    <span className="slider-label" />
                )}

                {(variant === "icon") && (
                    <>
                        <RiCheckLine
                            className="slider-icon-checked"
                            role="radio"
                            aria-checked="false"
                        />
                        <RiCloseLine
                            className="slider-icon-unchecked"
                            role="radio"
                            aria-checked="true"
                        />
                    </>
                )}
                {variant === "radio" && (
                    <>
                        <RiRadioButtonLine
                            className="slider-radio-checked"
                            role="radio"
                            aria-checked="false"
                        />
                        <RiCircleLine
                            className="slider-radio-unchecked"
                            role="radio"
                            aria-checked="true"
                        />
                    </>
                )}
                {variant === "icon-boxes" && (
                    <>
                        <RiCheckLine
                            className="slider-radio-checked"
                            role="radio"
                            aria-checked="false"
                            aria-label="Selected"
                        />
                        <span
                            className="slider-radio-unchecked"
                            role="radio"
                            aria-checked="true"
                            aria-label="Unselected"
                        />
                        {isIntermediate && (
                            <RiSubtractLine
                                className="slider-radio-intermediate"
                                role="radio"
                                aria-checked="false"
                                aria-label="Selected"
                            />
                        )}
                    </>
                )}
            </span>
            {textPlacement === "end" && text
                ? <span className={classNames(`toggle-text-${textPlacement}`, `toggle-text-${textVariant}`)}>{text}</span>
                : undefined}
        </label>
    );
});

Toggle.defaultProps = {
    className: "",
    size: "medium",
    variant: "none",
    textPlacement: "end",
    labelClassName: "",
    textVariant: "primary",
};

export default Toggle;
